import Vue from 'vue'

export function useChatSocket(url) {
    const state = Vue.observable({
        socket: null,
        messages: [],
        isManualClose: false,
        reconnectTimer: null,
        heartbeatTimer: null,
        reconnectAttempts: 0,
        maxReconnectAttempts: 10,
        isOnline: navigator.onLine,
        readyState: WebSocket.CLOSED // 添加响应式的 readyState 属性
    })

    const baseReconnectInterval = 3000
    const maxReconnectInterval = 30000
    const heartbeatInterval = 10000

    /** 连接 WebSocket */
    const connect = () => {
        if (state.socket || state.isManualClose) return

        state.socket = new WebSocket(url)
        state.readyState = WebSocket.CONNECTING // 更新状态为连接中

        state.socket.onopen = () => {
            console.log('[WebSocket] 连接成功')
            state.readyState = WebSocket.OPEN // 更新状态为已连接
            state.reconnectAttempts = 0 // 重置重连尝试次数
            startHeartbeat()
        }

        state.socket.onmessage = event => {
            try {
                const data = JSON.parse(event.data)
                if (data.code === '0') return // 心跳响应
                state.messages.push(data)
            } catch (e) {
                console.warn('[WebSocket] 接收非 JSON 消息：', event.data)
            }
        }

        state.socket.onclose = () => {
            console.warn('[WebSocket] 连接关闭')
            state.readyState = WebSocket.CLOSED // 更新状态为已关闭
            state.socket = null // 清除 socket 对象
            cleanup()
            if (!state.isManualClose) {
                attemptReconnect()
            }
        }

        state.socket.onerror = err => {
            console.error('[WebSocket] 错误', err)
            state.readyState = WebSocket.CLOSING // 更新状态为关闭中
            state.socket && state.socket.close()
        }
    }

    /** 尝试重连 */
    const attemptReconnect = () => {
        // 检查是否超过最大重连次数
        if (state.reconnectAttempts >= state.maxReconnectAttempts) {
            console.error('[WebSocket] 已达到最大重连次数，停止重连')
            return
        }

        // 检查网络状态
        if (!state.isOnline) {
            console.log('[WebSocket] 网络断开，等待网络恢复')
            return
        }

        // 计算重连延迟时间（指数退避策略）
        const delay = Math.min(
            baseReconnectInterval * Math.pow(2, state.reconnectAttempts),
            maxReconnectInterval
        )

        state.reconnectAttempts++
        console.log(`[WebSocket] ${delay/1000}秒后尝试第${state.reconnectAttempts}次重连...`)
        
        state.reconnectTimer = setTimeout(() => {
            connect()
        }, delay)
    }

    /** 发送消息 */
    const sendMessage = (msg) => {
        if (state.socket && state.socket.readyState === WebSocket.OPEN) {
            state.socket.send(msg)
        }
    }

    /** 主动断开连接 */
    const disconnect = () => {
        state.isManualClose = true
        state.readyState = WebSocket.CLOSING // 更新状态为关闭中
        cleanup()
        if (state.socket) {
            state.socket.close()
            state.socket = null
        }
        // 移除网络状态监听
        removeNetworkListeners()
    }

    /** 添加网络状态监听 */
    const addNetworkListeners = () => {
        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)
    }

    /** 移除网络状态监听 */
    const removeNetworkListeners = () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
    }

    /** 网络连接恢复处理 */
    const handleOnline = () => {
        console.log('[WebSocket] 网络已连接')
        state.isOnline = true
        // 如果WebSocket未连接且不是手动关闭，尝试重连
        if (!state.socket && !state.isManualClose) {
            state.reconnectAttempts = 0 // 重置重连尝试次数
            attemptReconnect()
        }
    }

    /** 网络断开处理 */
    const handleOffline = () => {
        console.log('[WebSocket] 网络已断开')
        state.isOnline = false
        // 清理重连定时器，因为网络已断开
        if (state.reconnectTimer) {
            clearTimeout(state.reconnectTimer)
            state.reconnectTimer = null
        }
    }

    /** 启动心跳 */
    const startHeartbeat = () => {
        stopHeartbeat()
        state.heartbeatTimer = setInterval(() => {
            if (state.socket && state.socket.readyState === WebSocket.OPEN) {
                state.socket.send(JSON.stringify({ code: '0' }))
            }
        }, heartbeatInterval)
    }

    /** 停止心跳 */
    const stopHeartbeat = () => {
        if (state.heartbeatTimer) {
            clearInterval(state.heartbeatTimer)
            state.heartbeatTimer = null
        }
    }

    /** 清理定时器 */
    const cleanup = () => {
        if (state.reconnectTimer) {
            clearTimeout(state.reconnectTimer)
            state.reconnectTimer = null
        }
        stopHeartbeat()
    }

    // 初始化时添加网络状态监听
    addNetworkListeners()

    return {
        connect,
        disconnect,
        sendMessage,
        state
    }
}